import { type ITranslation } from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import { descriptionHistoryLoad } from '@reducers/product/reducer';
import { type IDescriptionHistoryEntry } from '@reducers/product/types';
import {
  Drawer,
  type GlobalToken,
  Grid,
  Space,
  Spin,
  Switch,
  Tag,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { type Change, diffLines } from 'diff';
import React, { type CSSProperties, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { EDateFormat } from '../../fragments/date-format';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { FONT_SIZE, SPACING } from '../../theme/tokens';
import CopyToClipboard from '../copy-to-clipboard';

const { Text } = Typography;

const styleCode: CSSProperties = {
  fontFamily: 'monospace',
  fontSize: FONT_SIZE.sm,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  lineHeight: '1.5',
};

const getStyleAdded = (token: GlobalToken): CSSProperties => ({
  ...styleCode,
  backgroundColor: token.colorSuccessBg,
  color: token.colorSuccessText,
  display: 'block',
  padding: '0 4px',
});

const getStyleRemoved = (token: GlobalToken): CSSProperties => ({
  ...styleCode,
  backgroundColor: token.colorErrorBg,
  color: token.colorErrorText,
  display: 'block',
  padding: '0 4px',
});

const styleUnchanged: CSSProperties = {
  ...styleCode,
  display: 'block',
  padding: '0 4px',
  opacity: 0.5,
};

const getStyleDiffBlock = (token: GlobalToken): CSSProperties => ({
  border: `1px solid ${token.colorBorderSecondary}`,
  borderRadius: token.borderRadius,
  overflow: 'hidden',
  marginTop: SPACING.sm,
});

const getStyleEntry = (token: GlobalToken): CSSProperties => ({
  marginBottom: SPACING.xl,
  borderBottom: `1px solid ${token.colorBorderSecondary}`,
  paddingBottom: SPACING.lg,
});

const getStylePlainText = (token: GlobalToken): CSSProperties => ({
  ...styleCode,
  display: 'block',
  padding: SPACING.sm,
  border: `1px solid ${token.colorBorderSecondary}`,
  borderRadius: token.borderRadius,
  marginTop: SPACING.sm,
});

function DiffView({
  oldText,
  newText,
}: {
  oldText: string;
  newText: string;
}): React.ReactElement {
  const { token } = theme.useToken();
  const changes: Change[] = diffLines(oldText, newText);
  const hasChanges = changes.some((c) => c.added || c.removed);

  if (!hasChanges) {
    return <Text type="secondary">No diff — identical to current version</Text>;
  }

  return (
    <div style={getStyleDiffBlock(token)}>
      {changes.map((change, i) => {
        const lines = (change.value ?? '')
          .split('\n')
          .filter((l, idx, arr) => idx < arr.length - 1 || l !== '');
        const prefix = change.added ? '+ ' : change.removed ? '- ' : '  ';
        const style = change.added
          ? getStyleAdded(token)
          : change.removed
            ? getStyleRemoved(token)
            : styleUnchanged;
        return lines.map((line, j) => (
          <span key={`${i}-${j}`} style={style}>
            {prefix}
            {line}
          </span>
        ));
      })}
    </div>
  );
}

function DescriptionHistoryDrawer({
  open,
  onClose,
  productUUID,
  languageCode,
  currentDescriptions,
}: {
  open: boolean;
  onClose: () => void;
  productUUID: string;
  languageCode: string;
  currentDescriptions: ITranslation[];
}): React.ReactElement {
  const dispatch = useAppDispatch();
  const { token } = theme.useToken();
  const { md: isDesktop } = Grid.useBreakpoint();
  const history = useSelector((s: RootState) => s.Product.descriptionHistory);
  const loading = useSelector(
    (s: RootState) => s.Product.descriptionHistoryInProcess,
  );

  const [showDiff, setShowDiff] = useState(true);

  useEffect(() => {
    if (open) {
      dispatch(descriptionHistoryLoad(productUUID));
    }
  }, [open, productUUID, dispatch]);

  const filtered = useMemo(
    () =>
      history.filter(
        (h: IDescriptionHistoryEntry) => h.languageType.code === languageCode,
      ),
    [history, languageCode],
  );

  const currentDescription = useMemo(
    () => currentDescriptions.find((t) => t.code === languageCode)?.value ?? '',
    [currentDescriptions, languageCode],
  );

  return (
    <Drawer
      title={`Description History — ${languageCode.toUpperCase()}`}
      open={open}
      onClose={onClose}
      styles={{ wrapper: { width: isDesktop ? '75%' : '95%' } }}
      extra={
        <Space>
          <Text type="secondary">Diff</Text>
          <Switch size="small" checked={showDiff} onChange={setShowDiff} />
        </Space>
      }
    >
      <Spin spinning={loading}>
        {!loading && filtered.length === 0 ? (
          <Text type="secondary">No history entries found.</Text>
        ) : (
          filtered.map((entry, i) => (
            <div key={i} style={getStyleEntry(token)}>
              <Space>
                <Tag color="blue">
                  {dayjs(entry.createdAt).format(EDateFormat.DATE_TIME)}
                </Tag>
                <Tooltip title="Days ago">
                  <Text type="secondary">
                    ({dayjs().diff(dayjs(entry.createdAt), 'day')}d ago)
                  </Text>
                </Tooltip>
                <CopyToClipboard
                  text={entry.description}
                  title="Copy description"
                  size="small"
                  type="link"
                />
              </Space>
              {showDiff ? (
                <DiffView
                  oldText={entry.description ?? ''}
                  newText={currentDescription}
                />
              ) : (
                <span style={getStylePlainText(token)}>
                  {entry.description}
                </span>
              )}
            </div>
          ))
        )}
      </Spin>
    </Drawer>
  );
}

export default DescriptionHistoryDrawer;
